import { MAX_CATCHUP_MS, TICK_MS } from '@d2/core';

/**
 * What the browser's clock reads on the *server's* timeline.
 *
 * `./interpolation` takes a render time on the sample timeline and `./prediction`
 * files every predicted position under a server tick. Both read no clock at all
 * — deliberately, because a module that called `performance.now()` could not be
 * replayed frame by frame in a test — and both say in as many words that mapping
 * the browser's clock onto the server's is somebody else's job (`TODO.md` #160,
 * #183). This is that somebody. Observations in, an offset out, and no opinion
 * about positions, ticks, entities or sockets.
 *
 * Until this exists neither caller can be *used*, only tested: the buffer cannot
 * know what "100 ms behind the server" means in browser time, and prediction
 * reconciling a snapshot for tick *T* against a raw tick has its whole history
 * shifted by the round trip — which shows up as a snap-back after every click on
 * a slow link, indistinguishable from a reconciliation bug (`TODO.md` #183) and
 * the reason row 180 cannot be re-litigated from playtesting until this lands.
 *
 * ## The one thing a packet can tell you, and the one it cannot
 *
 * A packet is stamped with the sender's clock reading and arrives some unknown
 * time later. So
 *
 * ```
 * serverTimeMs - localTimeMs  =  trueOffset - delay
 * ```
 *
 * and since a delay is never negative, **every observation is short of the truth
 * and none is ever over it**. That asymmetry is the whole design: an average of
 * observations converges on the *average* delay, which is a number about the
 * network; the **maximum** converges on the truth less the *minimum* delay,
 * which is as close to the clock as a one-way stamp can get. The least delayed
 * packet is the only honest witness, so the extreme is what gets tracked.
 *
 * The estimate is therefore biased low by the stream's minimum one-way delay,
 * and it is worth knowing which way that cuts: the client believes the server is
 * slightly *earlier* than it really is, so it renders a touch further behind and
 * predicts a touch less far ahead. Both are the safe direction — the alternative
 * is a buffer that runs dry and a client that thinks it is ahead of a server it
 * is not. Measuring the round trip would close the gap and needs a delay filter
 * over `pong` rather than this one-sided maximum; that is `TODO.md` #190.
 *
 * ## Why not just take the newest packet
 *
 * The one-line estimator is `serverTime + (now - whenItArrived)`, and it
 * re-phases the entire render clock onto every packet: a patch 15 ms late drags
 * the clock 15 ms backwards and every interpolated entity with it, twenty times
 * a second. That is precisely the stutter the interpolation buffer exists to
 * remove, reintroduced one layer up. Jitter must not reach the render clock at
 * all, which rules out following any single packet — including a good one.
 *
 * ## The four decisions, and why
 *
 * **Slew, do not jump — at most 5% of real time.** The extreme is the *target*;
 * the reported offset walks towards it at `OFFSET_SLEW_RATE` and never faster.
 * A rate below 1 is what makes the render clock monotonic: over a 16.7 ms frame
 * the offset moves at most 0.83 ms, so the clock always advances, between 0.95
 * and 1.05 of real time. At `DEFAULT_MOVE_SPEED` that is 0.0033 of a cell of
 * extra motion in a frame — a fifth of a pixel at the 64×32 projection — against
 * the 3-4 pixels of *backwards* motion one late packet buys the naive version.
 * A correction is spread across frames rather than dropped on one, which is the
 * entire difference between smoothing jitter and rendering it.
 *
 * **Decay the extreme, slowly — a millisecond per second.** A maximum has no way
 * back down: one freakishly early packet would pin the estimate for the rest of
 * the session, and a genuine clock drift (a consumer crystal is good to tens of
 * ppm, not zero) would never be followed once the two clocks separated in the
 * unfavourable direction. The other direction needs nothing — a packet that
 * arrives *less* delayed than the extreme is adopted the instant it lands,
 * because it is strictly better evidence. So the decay is one-way and it is
 * slow: at `OFFSET_DECAY_RATE` it discards 1 ms of claim per second, which is a
 * thousand ppm — an order of magnitude faster than the worst crystal, so any
 * real drift is followed, and slow enough that a live 20 Hz stream re-earns the
 * extreme every packet and the estimate does not move at all.
 *
 * **The first observation is adopted whole.** There is no history to slew from
 * and nothing to be smooth against, and the gap being crossed is not a network
 * error but an *epoch*: `performance.now()` counts from page load and a server
 * clock counts from whenever it booted, so slewing from zero at 5% would take
 * hours. One packet with an unknown delay is a bad estimate, and it is also the
 * best one in existence at that moment; the stream corrects it within a few
 * packets. `isSynced` is false until then and the queries answer `null` rather
 * than a plausible-looking zero — the same call `./interpolation` makes for an
 * entity that has never reported a position.
 *
 * **A discontinuity is stepped, not slewed, and needs corroboration first.** A
 * machine that slept, a server that restarted, a route that changed: the offset
 * moves by seconds or hours at once, and slewing that at 5% would take from
 * minutes to a full day — a bug of its own. Both directions are answered, and
 * they are not symmetric:
 *
 *   - **Upward** — the packet claims the server is *further ahead* than the
 *     extreme says. No delay can produce that, so it is adopted at once, and
 *     stepped rather than slewed when it is more than `RESYNC_THRESHOLD_MS` out.
 *     One packet, no waiting.
 *   - **Downward** — the packet claims far *less*, which is exactly what a badly
 *     delayed packet also claims, and no single packet distinguishes the two. So
 *     it takes a run: observations that all disagree with the extreme by more
 *     than `RESYNC_THRESHOLD_MS`, continuously, for `RESYNC_HOLD_MS`. One
 *     ordinary packet ends the run and nothing was decided. A stall cannot fake
 *     it — a room rides one TCP connection, so a stalled packet arrives with
 *     everything queued behind it in a burst that is milliseconds wide in *local*
 *     time and never spans the hold. Once corroborated the run's own extreme is
 *     taken whole, history dropped: it belongs to a timeline that no longer
 *     exists.
 *
 * That bounds recovery from *any* discontinuity at `RESYNC_HOLD_MS` plus one
 * packet — about 300 ms at 20 Hz — instead of at its size over the slew rate.
 * The threshold is `MAX_CATCHUP_MS` rather than a number of this module's own:
 * core already decided that past a quarter second behind, catching up is the
 * wrong idea and the authoritative state *is* the correction (`tick.ts`,
 * `ARCHITECTURE.md`), and a render clock a quarter second out is past what any
 * amount of smoothing hides. The visible cost is one hitch, once, and then a
 * correct clock. Note the same rule adapts to a sustained delay increase — a
 * route change that adds 300 ms genuinely *has* moved the reachable minimum, and
 * waiting five minutes for the decay to walk there is the bug this avoids.
 *
 * ## Time is a parameter, here too
 *
 * Nothing in this module reads a clock — not `performance.now()`, not core's
 * `Clock` port. `TODO.md` #160 guessed this module would want the port; taking a
 * time as an argument instead is strictly weaker and strictly more testable,
 * which is the same call `interpolation.ts` and `prediction.ts` already made and
 * the reason a whole minute of jittery network can be replayed in a unit test.
 * The caller reads its clock once per frame, hands the reading in, and owns the
 * adapter (`TODO.md` #191).
 *
 * Every number here is **milliseconds**. No cell coordinates, no pixels.
 */

/**
 * How fast the reported offset walks towards the extreme, as a fraction of real
 * time. **5% — a millisecond of correction per twenty of clock.**
 *
 * The ceiling is 1: at that rate the render clock could stand still, and above
 * it, run backwards. Everything below is a trade between how visibly the world
 * runs fast or slow and how long a correction takes. 5% takes 400 ms to absorb a
 * typical 20 ms jitter excursion and at most 5 s for the largest error that is
 * not a discontinuity (`RESYNC_THRESHOLD_MS` over this rate), while distorting
 * motion by a twentieth — invisible on a walk cycle, and an order of magnitude
 * less than the frame-to-frame variation the browser hands out anyway.
 *
 * A placeholder in the sense `INTENT_TIMEOUT_TICKS` is (`TODO.md` #181, #193):
 * reasoned about rather than measured, because nothing has watched this run on a
 * real connection yet.
 */
export const OFFSET_SLEW_RATE = 0.05;

/**
 * How fast the extreme gives up its claim when nothing re-earns it. **A
 * millisecond per second.**
 *
 * This is the memory of the estimator, and it has to sit between two rates. Too
 * fast and it forgets good evidence between lucky packets, which lets ordinary
 * jitter drag the estimate down towards the average — the failure the maximum
 * exists to prevent. Too slow and a clock drift is never followed and a single
 * early packet rules the session. 1000 ppm clears both: an order of magnitude
 * over the worst crystal drift a machine will show, and a fiftieth of a
 * millisecond between two 20 Hz packets, so a live stream never decays at all.
 */
export const OFFSET_DECAY_RATE = 0.001;

/**
 * How far out the estimate must be before it is stepped instead of slewed, and
 * how far below the extreme an observation must fall before it is treated as
 * possible evidence of a new timeline. **`MAX_CATCHUP_MS` — a quarter second.**
 *
 * Core's own line, and for the same reason it was drawn there: past a quarter
 * second, catching up is the wrong idea and the authoritative value is the
 * correction rather than something to converge on (`tick.ts`). A render clock
 * 250 ms out is further than the interpolation buffer's whole retention window,
 * so nothing downstream can hide it either way.
 */
export const RESYNC_THRESHOLD_MS = MAX_CATCHUP_MS;

/**
 * How long observations must *keep* disagreeing before the estimate abandons its
 * extreme. **`MAX_CATCHUP_MS` — a quarter second, five snapshots at 20 Hz.**
 *
 * Long enough that no single event can fake it: a stalled connection releases
 * its queue in a burst milliseconds wide, so the run breaks on the first packet
 * that has caught up. Short enough that a slept machine or a restarted server is
 * back on the right timeline within a third of a second — the *point* of having
 * a step at all, since the alternative is a slew measured in hours.
 */
export const RESYNC_HOLD_MS = MAX_CATCHUP_MS;

/**
 * One packet's worth of evidence: what the sender's clock read, and when it
 * landed here.
 *
 * Both are milliseconds and neither has to share an epoch with the other — the
 * difference between them is the entire content. `serverTimeMs` is whatever the
 * caller can honestly stamp: `tickObservation` turns a `WorldState.tick` into
 * one, and `PongPayload.serverTime` is one already. `localTimeMs` must come from
 * the same monotonic source every time (`performance.now()`, never `Date.now()`,
 * which a user or an NTP step can move under you).
 */
export interface ClockObservation {
  /** The sender's clock reading the packet carries. */
  readonly serverTimeMs: number;
  /** The local clock reading when it arrived. */
  readonly localTimeMs: number;
}

/**
 * A run of observations that all disagree with the extreme by more than
 * `RESYNC_THRESHOLD_MS` — the timeline may have moved, and nothing is decided
 * until the run has lasted `RESYNC_HOLD_MS`.
 */
export interface ResyncCandidate {
  /** Local time of the first observation in the run. */
  readonly sinceMs: number;
  /** The least delayed observation of the run — what the estimate becomes if the run holds. */
  readonly offsetMs: number;
}

/**
 * The estimate, as a value.
 *
 * Immutable like the interpolation buffer and the prediction beside it:
 * `observe` returns a new estimate rather than writing into this one, so a frame
 * can never read a half-applied correction and a caller can keep one in a plain
 * field.
 *
 * `offsetMs` is the reported answer as it stood at `observedAtMs`; between
 * observations it keeps slewing, which is why every query takes a local time and
 * why reading this field directly is not the same as asking `offsetAt`.
 */
export interface ClockEstimate {
  /** Local → server offset at `observedAtMs`. Meaningless while `observations` is 0. */
  readonly offsetMs: number;
  /** The largest offset any observation has claimed, decayed by its age. What `offsetMs` is walking towards. */
  readonly peakOffsetMs: number;
  /** Local time of the newest observation folded in. */
  readonly observedAtMs: number;
  /** How many observations have been seen — adopted or not. Zero means nothing is known. */
  readonly observations: number;
  /** The resync run in progress, or `null` when the last observation agreed with the extreme. */
  readonly resync: ResyncCandidate | null;
}

const assertMilliseconds = (name: string, value: number): void => {
  // Phrased as a negated comparison so NaN — a clock read that was never a
  // number, or a subtraction of two that were not — is rejected along with the
  // infinities.
  if (!Number.isFinite(value)) {
    throw new RangeError(`A ${name} must be a finite number of milliseconds, received ${value}`);
  }
};

/** A clock that knows nothing. Every query answers `null` until the first observation. */
export const createClock = (): ClockEstimate => ({
  offsetMs: 0,
  peakOffsetMs: 0,
  observedAtMs: 0,
  observations: 0,
  resync: null,
});

/** Whether anything is known yet. `false` is not an error — it is the first second of a connection. */
export const isSynced = (estimate: ClockEstimate): boolean => estimate.observations > 0;

/**
 * A replicated `WorldState.tick` as an observation.
 *
 * The tick is the only server-side stamp a snapshot carries, and `tick *
 * TICK_MS` is its place on the server's millisecond timeline — the same
 * conversion `./interpolation` makes for exactly the same reason. It is a
 * *lower* bound on when the patch was sent: the room broadcasts after stepping,
 * so the true send time is one step plus a scheduling hiccup later, which shows
 * up as one more constant subtracted from the estimate. Constant, so the
 * maximum swallows it whole rather than jittering on it, and in the safe
 * direction — see `TODO.md` #190 for closing it with a real round trip.
 */
export const tickObservation = (tick: number, localTimeMs: number): ClockObservation => {
  if (!(Number.isInteger(tick) && tick >= 0)) {
    throw new RangeError(`An observed tick must be a whole, non-negative number, received ${tick}`);
  }
  assertMilliseconds('arrival time', localTimeMs);

  return { serverTimeMs: tick * TICK_MS, localTimeMs };
};

/**
 * The offset after slewing towards the extreme for however long it has been.
 *
 * Elapsed time is clamped at zero rather than rejected: a frame that read its
 * clock before a message handler ran is asking about a moment just before the
 * last packet, which is nonsense to slew backwards for and harmless to answer
 * with the offset as it stood.
 */
const slewed = (estimate: ClockEstimate, localTimeMs: number): number => {
  const elapsedMs = Math.max(0, localTimeMs - estimate.observedAtMs);
  const gapMs = estimate.peakOffsetMs - estimate.offsetMs;

  return estimate.offsetMs + Math.sign(gapMs) * Math.min(Math.abs(gapMs), OFFSET_SLEW_RATE * elapsedMs);
};

/**
 * How far the local clock is behind the server's at `localTimeMs`, or `null`
 * while nothing has been observed.
 *
 * Pure: the same local time always gives the same offset, so a frame can be
 * redrawn or replayed. `null` rather than zero, because zero is a claim and an
 * unsynced clock has none to make.
 */
export const offsetAt = (estimate: ClockEstimate, localTimeMs: number): number | null => {
  assertMilliseconds('local time', localTimeMs);

  return isSynced(estimate) ? slewed(estimate, localTimeMs) : null;
};

/**
 * What the server's clock reads at `localTimeMs` — the render time
 * `./interpolation` wants, on the timeline `positionAt` already speaks.
 *
 * Non-decreasing in `localTimeMs` by construction, since `OFFSET_SLEW_RATE` is
 * below 1. The one exception is a resync, which moves the timeline itself and is
 * meant to be a step.
 */
export const serverTimeAt = (estimate: ClockEstimate, localTimeMs: number): number | null => {
  const offsetMs = offsetAt(estimate, localTimeMs);

  return offsetMs === null ? null : localTimeMs + offsetMs;
};

/**
 * The server tick in progress at `localTimeMs` — what `./prediction` anchors its
 * own counter to, so that a snapshot for tick *T* is compared against what this
 * client thought at *T* rather than against what it thinks now.
 *
 * Floored, because a tick owns the 50 ms after its stamp: the server simulating
 * tick *T* is anywhere in `[T * TICK_MS, (T + 1) * TICK_MS)`.
 */
export const serverTickAt = (estimate: ClockEstimate, localTimeMs: number): number | null => {
  const serverTimeMs = serverTimeAt(estimate, localTimeMs);

  return serverTimeMs === null ? null : Math.floor(serverTimeMs / TICK_MS);
};

/**
 * Folds one packet in.
 *
 * The whole policy, in order: seed on the first observation; age the extreme by
 * however long it has been; then either open or extend a resync run for an
 * observation that falls far below it, or take the maximum of the extreme and
 * this observation and walk the reported offset towards it.
 *
 * An arrival earlier than the last one throws. It cannot happen — a room rides
 * one TCP connection and the arrival time is read here, on one monotonic clock —
 * so it means the caller mixed two clocks, which is a programmer error and not a
 * game outcome. Two arrivals on the same millisecond are ordinary and accepted.
 */
export const observe = (estimate: ClockEstimate, observation: ClockObservation): ClockEstimate => {
  assertMilliseconds('server time', observation.serverTimeMs);
  assertMilliseconds('arrival time', observation.localTimeMs);

  const { serverTimeMs, localTimeMs } = observation;
  const arrivalOffsetMs = serverTimeMs - localTimeMs;

  if (!isSynced(estimate)) {
    return {
      offsetMs: arrivalOffsetMs,
      peakOffsetMs: arrivalOffsetMs,
      observedAtMs: localTimeMs,
      observations: 1,
      resync: null,
    };
  }

  if (localTimeMs < estimate.observedAtMs) {
    throw new RangeError(
      `An arrival time may not precede the previous one — ${localTimeMs} follows ${estimate.observedAtMs}`,
    );
  }

  const carriedMs = slewed(estimate, localTimeMs);
  const decayedPeakMs = estimate.peakOffsetMs - OFFSET_DECAY_RATE * (localTimeMs - estimate.observedAtMs);
  const observations = estimate.observations + 1;

  if (decayedPeakMs - arrivalOffsetMs > RESYNC_THRESHOLD_MS) {
    const opened = estimate.resync ?? { sinceMs: localTimeMs, offsetMs: arrivalOffsetMs };
    const resync: ResyncCandidate = {
      sinceMs: opened.sinceMs,
      offsetMs: Math.max(opened.offsetMs, arrivalOffsetMs),
    };

    // Corroborated: the old extreme describes a timeline that is gone, so it is
    // dropped rather than decayed away, and the run's own least delayed packet
    // is taken whole.
    if (localTimeMs - resync.sinceMs >= RESYNC_HOLD_MS) {
      return {
        offsetMs: resync.offsetMs,
        peakOffsetMs: resync.offsetMs,
        observedAtMs: localTimeMs,
        observations,
        resync: null,
      };
    }

    return { offsetMs: carriedMs, peakOffsetMs: decayedPeakMs, observedAtMs: localTimeMs, observations, resync };
  }

  const peakOffsetMs = Math.max(decayedPeakMs, arrivalOffsetMs);

  return {
    // Past the threshold there is nothing to be smooth about: the step is the
    // same call core makes for a capped catch-up, and the alternative is minutes
    // of slewing on a clock that is already unusable.
    offsetMs: Math.abs(peakOffsetMs - carriedMs) > RESYNC_THRESHOLD_MS ? peakOffsetMs : carriedMs,
    peakOffsetMs,
    observedAtMs: localTimeMs,
    observations,
    resync: null,
  };
};
